import { InstagramIcon } from '@/components/ui/icons/InstagramIcon'
import { LinkedInIcon } from '@/components/ui/icons/LinkedInIcon'
import { FacebookIcon } from '@/components/ui/icons/FacebookIcon'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar'
import { Button } from '@/components/ui/shadcn/button'
import { Card, CardContent, CardHeader } from '@/components/ui/shadcn/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/shadcn/alert-dialog'
import type {
  Accounts,
  PlatformSyncStatus,
  PublishablePlatformType,
} from '@backend/lib/db/schema/platform'
import { AlertTriangle, ArrowRight, LogOut, Shield, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { AccountCard, AccountCardContent } from '../account-card/account-card'
import { TikTokIcon } from '@/components/ui/icons/TikTokIcon'
import { GoogleAuthButton } from '@/components/auth/auth-google'
import { YoutubeIcon } from '@/components/ui/icons/YoutubeIcon'
import { SCOPE as YOUTUBE_SCOPE } from '@/lib/auth/youtube'

/** Up to two initials for a connected profile, shown when the platform gives us
 *  no avatar or the image fails to load. Splits on code points rather than code
 *  units because social display names routinely start with an emoji. Falls back
 *  to a neutral glyph for handle-only accounts with no display name. */
const profileInitials = (displayName: string | undefined): string => {
  const words = (displayName ?? '').trim().split(/\s+/).filter(Boolean)
  const initials = words
    .slice(0, 2)
    .map((word) => [...word][0]?.toUpperCase() ?? '')
    .join('')
  return initials || '?'
}

/** Strip common URL prefixes so scope names fit in the tooltip. */
const formatScope = (scope: string) => scope.replace('https://www.googleapis.com/auth/', '')

interface Props {
  platform: PublishablePlatformType
  isLoading: boolean
  accounts: Accounts | undefined
  expiresAt: number | undefined
  refreshTokenExpiresAt?: number
  scopes?: string[]
  analyticsAvailable?: boolean
  syncStatus?: PlatformSyncStatus | null
  syncStatusDetail?: string | null
  // tRPC serializes Date to string on the wire; accept both shapes so callers
  // can pass the query payload straight through without manual coercion.
  syncStatusAt?: Date | string | null
  onStartAuth?: () => void
  onSignOut: () => void
  onToggleAutoImport?: (accountId: string, enabled: boolean) => void
  onRemoveAccount?: (accountId: string) => void
}

const SYNC_STATUS_LABEL: Record<Exclude<PlatformSyncStatus, 'ok'>, string> = {
  needs_reauth: 'Connection expired — reconnect required',
  missing_scopes: 'Missing analytics permissions',
  rate_limited: 'Rate-limited by the platform — sync will retry',
  error: 'Last sync ran into an error — sync will retry',
}

export function AccountTabSocialMediaConnection({
  platform,
  isLoading,
  accounts,
  expiresAt,
  refreshTokenExpiresAt,
  scopes,
  onStartAuth,
  onSignOut,
  analyticsAvailable,
  syncStatus,
  syncStatusDetail,
  syncStatusAt,
  onToggleAutoImport,
  onRemoveAccount,
}: Props) {
  const syncIssue = syncStatus && syncStatus !== 'ok' ? syncStatus : null
  // Platform-specific display elements
  const platformInfo = {
    linkedin: {
      icon: () => <LinkedInIcon width="32" height="32" />,
      connectText: 'LinkedIn',
      signInText: 'Connect your LinkedIn account',
      loadingText: 'LinkedIn is connecting...',
    },
    instagram: {
      icon: () => <InstagramIcon width="32" height="32" />,
      connectText: 'Instagram',
      signInText: 'Connect your Instagram account',
      loadingText: 'Instagram is connecting...',
    },
    tiktok: {
      icon: () => <TikTokIcon width="32" height="32" />,
      connectText: 'TikTok',
      signInText: 'Connect your TikTok account',
      loadingText: 'TikTok is connecting...',
    },
    youtube: {
      icon: () => <YoutubeIcon width="40" height="40" />,
      connectText: 'YouTube',
      signInText: 'Connect your YouTube account',
      loadingText: 'YouTube is connecting...',
    },
    facebook: {
      icon: () => <FacebookIcon width="32" height="32" />,
      connectText: 'Facebook',
      signInText: 'Connect your Facebook account',
      loadingText: 'Facebook is connecting...',
    },
  }

  const PlatformIcon = platformInfo[platform].icon

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex h-full flex-col justify-between space-y-1">
          <span className="flex items-center gap-2">
            <PlatformIcon />
            <h3 className="text-xl font-bold">Checking if account is connected...</h3>
          </span>
          <p className="font-regular text-base">{platformInfo[platform].loadingText}</p>
        </CardContent>
      </Card>
    )
  }

  // Not connected state
  if (accounts === undefined || accounts.length === 0) {
    return (
      <AccountCard>
        <AccountCardContent className="l flex flex-col space-y-2">
          <div className="flex h-full flex-row items-center justify-between space-x-2">
            <span className="flex items-center gap-2">
              <PlatformIcon />
              <h3 className="text-xl font-bold">{platformInfo[platform].connectText}</h3>
            </span>
            <div>
              {platform === 'youtube' ? (
                <GoogleAuthButton
                  referrerPath={window.location.pathname}
                  flow="youtube"
                  scope={YOUTUBE_SCOPE}
                  buttonProps={{
                    variant: 'primary',
                    size: 'default',
                    icon: null,
                    children: (
                      <>
                        Connect
                        <ArrowRight className="pl-1" />
                      </>
                    ),
                  }}
                />
              ) : (
                <Button onClick={onStartAuth}>
                  Connect
                  <ArrowRight className="pl-1" />
                </Button>
              )}
            </div>
          </div>
          <p className="font-regular text-base">{platformInfo[platform].signInText}</p>
        </AccountCardContent>
      </AccountCard>
    )
  }

  // Connected state with accounts
  return (
    <div className="space-y-2">
      <AccountCard>
        <CardHeader className="flex flex-col justify-between space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PlatformIcon />
              <h3 className="text-xl font-bold">{platformInfo[platform].connectText}</h3>
            </div>
            <Button variant="outline" onClick={onSignOut}>
              Sign Out
              <LogOut className="pl-1" />
            </Button>
          </div>
          <div className="flex flex-col">
            <p className="font-regular text-base">
              Your {platformInfo[platform].connectText} account is connected
            </p>
            {refreshTokenExpiresAt ? (
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">
                  Reconnection required by {new Date(refreshTokenExpiresAt).toLocaleDateString()}
                </p>
              </div>
            ) : expiresAt ? (
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground text-sm">
                  Expires on {new Date(expiresAt).toLocaleDateString()}
                </p>
              </div>
            ) : null}
            {syncIssue && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex w-fit cursor-default items-center gap-1 text-sm text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>{SYNC_STATUS_LABEL[syncIssue]}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs">
                    {syncStatusDetail && <p className="font-mono text-xs">{syncStatusDetail}</p>}
                    {syncStatusAt && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        Detected{' '}
                        {formatDistanceToNow(
                          syncStatusAt instanceof Date ? syncStatusAt : new Date(syncStatusAt),
                          { addSuffix: true },
                        )}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {scopes && scopes.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-muted-foreground flex w-fit cursor-default items-center gap-1 text-sm">
                      <Shield className="h-3.5 w-3.5" />
                      <span>{scopes.length} permissions granted</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start">
                    <ul className="space-y-0.5">
                      {scopes.map((scope) => (
                        <li key={scope} className="font-mono">
                          {formatScope(scope)}
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col justify-between space-y-2">
          {accounts.map((account) => (
            <Card key={account.id}>
              <CardContent className="flex items-center justify-between gap-4">
                <span className="flex min-w-0 flex-1 items-center gap-4">
                  <Avatar>
                    <AvatarImage src={account.imageUrl} alt={account.displayName} />
                    <AvatarFallback className="text-foreground text-xs font-semibold">
                      {profileInitials(account.displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <span>
                    <p className="text-base font-semibold">{account.displayName}</p>
                    <p className="text-sm font-light">{account.subline}</p>
                  </span>
                </span>
                <div className="flex shrink-0 items-center gap-3">
                  {onToggleAutoImport &&
                    (() => {
                      const isPersonalLinkedIn =
                        platform === 'linkedin' && account.id.startsWith('urn:li:person:')
                      const disabled = isPersonalLinkedIn || analyticsAvailable === false
                      const tooltipText = isPersonalLinkedIn
                        ? 'Auto-import is not available for personal LinkedIn profiles'
                        : analyticsAvailable === false
                          ? 'Reconnect your account to grant the required permissions'
                          : 'Automatically import new posts every 6 hours'

                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1.5">
                                <Checkbox
                                  id={`auto-import-${account.id}`}
                                  checked={!disabled && (account.autoImport ?? false)}
                                  disabled={disabled}
                                  onCheckedChange={(checked) =>
                                    onToggleAutoImport(account.id, checked === true)
                                  }
                                />
                                <label
                                  htmlFor={`auto-import-${account.id}`}
                                  className={`whitespace-nowrap text-xs ${disabled ? 'text-muted-foreground/50 cursor-not-allowed' : 'text-muted-foreground cursor-pointer'}`}>
                                  Auto-import
                                </label>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>{tooltipText}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    })()}
                  {onRemoveAccount && (
                    <AlertDialog>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-destructive h-8 w-8">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Remove account</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {account.displayName}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will disconnect this account. You can reconnect it at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 text-white hover:bg-red-700"
                            onClick={() => onRemoveAccount(account.id)}>
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </AccountCard>
    </div>
  )
}
